<template>
  <div class="applicant-form">
    <div class="card shadow-lg">
      <div class="card-body p-4">
        <h2 class="card-title mb-4 text-gradient">Ihre Daten</h2>
        <p class="text-muted mb-4">
          Bitte füllen Sie das Formular aus, um mit dem Test zu beginnen.
        </p>

        <form @submit.prevent="handleSubmit" novalidate class="needs-validation">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="gender" class="fw-semibold">Anrede <span class="text-danger">*</span></label>
                <div class="custom-input">
                  <select
                    class="form-select"
                    id="gender"
                    v-model="applicant.gender"
                    required
                    :class="{ 'is-invalid': submitted && !applicant.gender }"
                  >
                    <option value="" disabled selected>Bitte wählen...</option>
                    <option value="male">Herr</option>
                    <option value="female">Frau</option>
                    <option value="diverse">Divers</option>
                  </select>
                  <div class="invalid-feedback" v-if="submitted && !applicant.gender">
                    Bitte wählen Sie eine Anrede aus.
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="title" class="fw-semibold">Titel</label>
                <div class="custom-input">
                  <input
                    type="text"
                    class="form-control"
                    id="title"
                    v-model="applicant.title"
                    placeholder="z.B. Dr., Prof., etc."
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="firstName" class="fw-semibold">Vorname <span class="text-danger">*</span></label>
                <div class="custom-input">
                  <input
                    type="text"
                    class="form-control"
                    id="firstName"
                    v-model="applicant.first_name"
                    required
                    placeholder="Ihr Vorname"
                    :class="{ 'is-invalid': submitted && !applicant.first_name }"
                  />
                  <div class="invalid-feedback" v-if="submitted && !applicant.first_name">
                    Bitte geben Sie Ihren Vornamen ein.
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="lastName" class="fw-semibold">Nachname <span class="text-danger">*</span></label>
                <div class="custom-input">
                  <input
                    type="text"
                    class="form-control"
                    id="lastName"
                    v-model="applicant.last_name"
                    required
                    placeholder="Ihr Nachname"
                    :class="{ 'is-invalid': submitted && !applicant.last_name }"
                  />
                  <div class="invalid-feedback" v-if="submitted && !applicant.last_name">
                    Bitte geben Sie Ihren Nachnamen ein.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="email" class="fw-semibold">E-Mail <span class="text-danger">*</span></label>
                <div class="custom-input">
                  <input
                    type="email"
                    class="form-control"
                    id="email"
                    v-model="applicant.email"
                    required
                    placeholder="Ihre E-Mail-Adresse"
                    :class="{ 'is-invalid': submitted && (!applicant.email || !isValidEmail) }"
                  />
                  <div class="invalid-feedback" v-if="submitted && !applicant.email">
                    Bitte geben Sie Ihre E-Mail-Adresse ein.
                  </div>
                  <div class="invalid-feedback" v-if="submitted && applicant.email && !isValidEmail">
                    Bitte geben Sie eine gültige E-Mail-Adresse ein.
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="phone" class="fw-semibold">Telefon <span class="text-danger">*</span></label>
                <div class="custom-input">
                  <input
                    type="tel"
                    class="form-control"
                    id="phone"
                    v-model="applicant.phone"
                    required
                    placeholder="Ihre Telefonnummer"
                    :class="{ 'is-invalid': submitted && !applicant.phone }"
                  />
                  <div class="invalid-feedback" v-if="submitted && !applicant.phone">
                    Bitte geben Sie Ihre Telefonnummer ein.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-group mt-3">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                id="privacyPolicy"
                v-model="privacyAgreed"
                required
                :class="{ 'is-invalid': submitted && !privacyAgreed }"
              />
              <label class="form-check-label" for="privacyPolicy">
                Ich habe die <a href="#" @click.prevent="showPrivacyModal = true">Datenschutzerklärung</a> gelesen und akzeptiere diese <span class="text-danger">*</span>
              </label>
              <div class="invalid-feedback" v-if="submitted && !privacyAgreed">
                Sie müssen die Datenschutzerklärung akzeptieren, um fortzufahren.
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between mt-4">
            <button type="button" class="btn btn-outline-secondary" @click="$emit('cancel')">
              Abbrechen
            </button>
            <button type="submit" class="btn btn-primary px-4" :disabled="loading">
              <span class="spinner-border spinner-border-sm me-2" v-if="loading"></span>
              <i class="fas fa-arrow-right me-2" v-else></i>Test starten
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Datenschutz-Modal -->
    <div class="modal fade" :class="{ 'show d-block': showPrivacyModal }" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Datenschutzerklärung</h5>
            <button type="button" class="btn-close" @click="showPrivacyModal = false" aria-label="Schließen"></button>
          </div>
          <div class="modal-body">
            <h3>1. Datenschutz auf einen Blick</h3>
            <h4>Allgemeine Hinweise</h4>
            <p>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert,
              wenn Sie diesen Test absolvieren. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert
              werden können. Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem Text
              aufgeführten Datenschutzerklärung.
            </p>

            <h4>Datenerfassung während des Tests</h4>
            <p>
              Wir erheben die von Ihnen angegebenen personenbezogenen Daten (Name, E-Mail, Telefonnummer) ausschließlich
              zum Zweck der Durchführung des Tests und zur Kommunikation der Ergebnisse. Ihre Daten werden nicht an Dritte
              weitergegeben, außer an das Unternehmen, das diesen Test für Sie bereitgestellt hat.
            </p>

            <h4>Speicherdauer</h4>
            <p>
              Ihre Daten werden für die Dauer des Bewerbungsverfahrens gespeichert und anschließend nach den gesetzlichen
              Aufbewahrungsfristen gelöscht oder anonymisiert.
            </p>

            <h3>2. Ihre Rechte</h3>
            <p>
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer gespeicherten
              personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder Löschung dieser
              Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung
              jederzeit für die Zukunft widerrufen.
            </p>

            <h3>3. Kontakt</h3>
            <p>
              Bei Fragen zur Erhebung, Verarbeitung oder Nutzung Ihrer personenbezogenen Daten oder bei Auskunfts-,
              Berichtigungs- oder Löschungswünschen wenden Sie sich bitte an den Datenschutzbeauftragten des Unternehmens,
              das diesen Test für Sie bereitgestellt hat.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click="acceptPrivacy">Akzeptieren</button>
            <button type="button" class="btn btn-secondary" @click="showPrivacyModal = false">Schließen</button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" v-if="showPrivacyModal"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ApplicantForm',
  props: {
    testId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      applicant: {
        gender: '',
        title: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        test_id: this.testId
      },
      submitted: false,
      privacyAgreed: false,
      showPrivacyModal: false,
      loading: false
    };
  },
  computed: {
    isValidEmail() {
      // Einfache Regex für E-Mail-Validierung
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(this.applicant.email);
    }
  },
  methods: {
    handleSubmit() {
      this.submitted = true;

      // Validierung
      if (!this.applicant.gender || 
          !this.applicant.first_name || 
          !this.applicant.last_name || 
          !this.applicant.email || 
          !this.isValidEmail || 
          !this.applicant.phone || 
          !this.privacyAgreed) {
        return;
      }

      this.loading = true;
      
      // Daten für Eltern-Komponente vorbereiten
      const applicantData = {
        ...this.applicant,
        name: `${this.applicant.title ? this.applicant.title + ' ' : ''}${this.applicant.first_name} ${this.applicant.last_name}`,
        privacy_agreed: this.privacyAgreed
      };
      
      // Event emittieren, um die Daten an die Eltern-Komponente zu senden
      this.$emit('submit', applicantData);
      
      // Nach kurzer Verzögerung Loading zurücksetzen (für den Fall, dass die Elternkomponente nicht reagiert)
      setTimeout(() => {
        this.loading = false;
      }, 3000);
    },
    acceptPrivacy() {
      this.privacyAgreed = true;
      this.showPrivacyModal = false;
    },
    cancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
.applicant-form {
  max-width: 800px;
  margin: 0 auto;
}

.card {
  border: none;
  border-radius: var(--border-radius-lg);
  transition: all 0.3s ease;
  background-color: var(--card-bg);
}

.shadow-lg {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}

.text-gradient {
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.btn {
  border-radius: var(--border-radius);
  padding: var(--input-padding-y) var(--input-padding-x);
  font-weight: 500;
  transition: all 0.3s ease;
  
  &.btn-primary {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color-dark);
      border-color: var(--primary-color-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 114, 227, 0.35);
    }
  }
  
  &.btn-outline-secondary {
    color: var(--text-color);
    border-color: var(--border-color);
    
    &:hover {
      background-color: var(--light-color);
      color: var(--text-color);
      transform: translateY(-1px);
    }
  }
}

// Modal Styling
.modal {
  background-color: rgba(0, 0, 0, 0.5);
  
  &.show {
    display: block;
  }
}

.modal-content {
  border: none;
  border-radius: var(--border-radius-lg);
  background-color: var(--card-bg);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
}

.modal-header {
  border-bottom: 1px solid var(--border-color);
}

.modal-footer {
  border-top: 1px solid var(--border-color);
}

// Animation
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.applicant-form {
  animation: fadeIn 0.5s ease-in-out;
}
</style> 